import { useState, useEffect, useCallback, Fragment } from 'react';
import type { ReactNode } from 'react';
import { activityApi, ActivityLog } from '../../api/activity.api';
import {
  Paper,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Pagination,
  Button,
  Tooltip,
  Checkbox,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  History,
  Refresh,
  DeleteSweep,
  KeyboardArrowDown,
  KeyboardArrowRight,
  ContentCopy,
  Check,
  FilterAltOff,
} from '@mui/icons-material';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const typeColor = (
  type: string
): 'success' | 'warning' | 'info' | 'error' | 'default' | 'primary' => {
  switch (type) {
    case 'LOGIN':
      return 'success';
    case 'LOGOUT':
      return 'warning';
    case 'ACTION':
      return 'primary';
    case 'PAGE_VIEW':
      return 'info';
    case 'VIEW':
      return 'info';
    default:
      return 'default';
  }
};

// Extract the affected object's internal id from the metadata JSON, if present.
const extractEntityId = (metadata: string | null): string => {
  if (!metadata) return '';
  try {
    const parsed = JSON.parse(metadata);
    return parsed?.entityId || '';
  } catch {
    return '';
  }
};

// A dim label cell for the details grid.
const DetailLabel = ({ children }: { children: ReactNode }) => (
  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
    {children}
  </Typography>
);

// A monospace value with a copy-to-clipboard button (for IDs).
const MonoValue = ({
  value,
  copied,
  onCopy,
}: {
  value: string;
  copied: boolean;
  onCopy: () => void;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
      {value}
    </Typography>
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={onCopy} aria-label="Copy to clipboard">
        {copied ? <Check fontSize="inherit" color="success" /> : <ContentCopy fontSize="inherit" />}
      </IconButton>
    </Tooltip>
  </Box>
);

export const ActivityLogPanel = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Row expansion (show full details) + copy-to-clipboard feedback
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Row selection (checkboxes)
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Cleanup dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Convert a datetime-local string to ISO, or undefined if empty.
  const toIso = (v: string): string | undefined => (v ? new Date(v).toISOString() : undefined);

  const hasAnyFilter = Boolean(typeFilter || userFilter || fromFilter || toFilter);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await activityApi.getActivities({
        page,
        limit: pageSize,
        activityType: typeFilter || undefined,
        userId: userFilter || undefined,
        from: toIso(fromFilter),
        to: toIso(toFilter),
      });
      setLogs(response.data.data || []);
      setTotalPages(response.data.meta?.totalPages || 1);
      setTotal(response.data.meta?.total || 0);
      setSelectedIds([]); // reset selection whenever the list reloads
    } catch (err) {
      console.error('Failed to load activity logs:', err);
      setError('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, typeFilter, userFilter, fromFilter, toFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Load the filter options (types + users) that actually exist in the logs,
  // narrowed by the OTHER active filters, so the dropdowns only show relevant
  // values. Re-runs whenever any filter changes.
  useEffect(() => {
    activityApi
      .getFilterOptions({
        activityType: typeFilter || undefined,
        userId: userFilter || undefined,
        from: toIso(fromFilter),
        to: toIso(toFilter),
      })
      .then((res) => {
        setTypes(res.data.data?.types || []);
        setUsers(res.data.data?.users || []);
      })
      .catch(() => {
        // Keep whatever options we had; don't clear on transient errors.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, userFilter, fromFilter, toFilter]);

  const handleCleanup = async () => {
    try {
      setDeleting(true);
      setError('');
      setSuccess('');
      const res = await activityApi.deleteActivitiesByIds(selectedIds);
      const deleted = res.data.data?.deleted ?? 0;
      setSuccess(`Deleted ${deleted} activity log${deleted === 1 ? '' : 's'}`);
      setConfirmOpen(false);
      fetchLogs();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Failed to delete activity logs');
      } else {
        setError('Failed to delete activity logs');
      }
    } finally {
      setDeleting(false);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allOnPageSelected = logs.length > 0 && logs.every((l) => selectedIds.includes(l.id));
  const someOnPageSelected = logs.some((l) => selectedIds.includes(l.id)) && !allOnPageSelected;

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      // Unselect all rows currently shown
      const pageIds = new Set(logs.map((l) => l.id));
      setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      // Select all rows currently shown (merge with any already selected)
      setSelectedIds((prev) => Array.from(new Set([...prev, ...logs.map((l) => l.id)])));
    }
  };

  const clearFilters = () => {
    setTypeFilter('');
    setUserFilter('');
    setFromFilter('');
    setToFilter('');
    setPage(1);
  };

  const formatWhen = (iso: string) => new Date(iso).toLocaleString();

  const userLabel = (log: ActivityLog) => {
    if (log.user) {
      return `${log.user.firstName} ${log.user.lastName}`;
    }
    return log.userId ? 'Unknown user' : 'System';
  };

  // Parse the metadata JSON into a plain object for the details view.
  const parseMetadata = (metadata: string | null): Record<string, unknown> => {
    if (!metadata) return {};
    try {
      return JSON.parse(metadata) || {};
    } catch {
      return {};
    }
  };

  const copyToClipboard = async (value: string, rowId: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(rowId);
      setTimeout(() => setCopiedId((cur) => (cur === rowId ? null : cur)), 1500);
    } catch {
      // Clipboard may be unavailable (e.g. non-HTTPS); ignore silently.
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <History sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Activity Log</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button startIcon={<Refresh />} onClick={fetchLogs} disabled={loading} size="small">
            Refresh
          </Button>
          <Button
            startIcon={<DeleteSweep />}
            onClick={() => setConfirmOpen(true)}
            disabled={loading || selectedIds.length === 0}
            size="small"
            color="error"
          >
            Delete Selected{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Shows logins, logouts, page views, and actions across the site. Click a row to expand it
        and see full details, including the object's internal ID (with a copy button). Tick rows
        and use "Delete Selected" to clean up.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label="Activity type"
          value={typeFilter}
          onChange={(e) => {
            setPage(1);
            setTypeFilter(e.target.value);
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All types</MenuItem>
          {types.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="User"
          value={userFilter}
          onChange={(e) => {
            setPage(1);
            setUserFilter(e.target.value);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All users</MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.firstName} {u.lastName} ({u.email})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="datetime-local"
          label="From"
          value={fromFilter}
          onChange={(e) => {
            setPage(1);
            setFromFilter(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 210 }}
        />
        <TextField
          size="small"
          type="datetime-local"
          label="To"
          value={toFilter}
          onChange={(e) => {
            setPage(1);
            setToFilter(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 210 }}
        />
        <TextField
          select
          size="small"
          label="Per page"
          value={pageSize}
          onChange={(e) => {
            setPage(1);
            setPageSize(Number(e.target.value));
          }}
          sx={{ minWidth: 110 }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Clear all filters">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterAltOff />}
              onClick={clearFilters}
              disabled={!hasAnyFilter}
            >
              Clear
            </Button>
          </span>
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          {total} record{total === 1 ? '' : 's'}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No activity matches the current filters.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someOnPageSelected}
                      checked={allOnPageSelected}
                      onChange={toggleSelectAll}
                      inputProps={{ 'aria-label': 'Select all rows on this page' }}
                    />
                  </TableCell>
                  <TableCell padding="checkbox" />
                  <TableCell>When</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => {
                  const isExpanded = expandedId === log.id;
                  const meta = parseMetadata(log.metadata);
                  const entityId = extractEntityId(log.metadata);
                  return (
                    <Fragment key={log.id}>
                      <TableRow
                        hover
                        selected={selectedIds.includes(log.id)}
                        sx={{ '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(log.id)}
                            onChange={() => toggleOne(log.id)}
                            inputProps={{ 'aria-label': `Select activity ${log.id}` }}
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => setExpandedId(isExpanded ? null : log.id)}
                            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                          >
                            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatWhen(log.createdAt)}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{userLabel(log)}</Typography>
                            {log.user && (
                              <Typography variant="caption" color="text.secondary">
                                {log.user.email} · {log.user.role}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.activityType}
                            color={typeColor(log.activityType)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={log.activityDescription || ''}>
                            <Typography variant="body2" sx={{ maxWidth: 320 }} noWrap>
                              {log.activityDescription || '—'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {log.ipAddress ? log.ipAddress.replace(/^::ffff:/, '') : '—'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ py: 0, border: 0 }} colSpan={7}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 1, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Full details
                              </Typography>
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '160px 1fr',
                                  rowGap: 0.5,
                                  columnGap: 2,
                                  alignItems: 'center',
                                }}
                              >
                                <DetailLabel>Activity ID</DetailLabel>
                                <MonoValue
                                  value={log.id}
                                  copied={copiedId === `log-${log.id}`}
                                  onCopy={() => copyToClipboard(log.id, `log-${log.id}`)}
                                />

                                <DetailLabel>Timestamp</DetailLabel>
                                <Typography variant="body2">{formatWhen(log.createdAt)}</Typography>

                                <DetailLabel>Type</DetailLabel>
                                <Typography variant="body2">{log.activityType}</Typography>

                                <DetailLabel>Description</DetailLabel>
                                <Typography variant="body2">
                                  {log.activityDescription || '—'}
                                </Typography>

                                <DetailLabel>User</DetailLabel>
                                <Typography variant="body2">
                                  {userLabel(log)}
                                  {log.user ? ` (${log.user.email}, ${log.user.role})` : ''}
                                </Typography>

                                {entityId && (
                                  <>
                                    <DetailLabel>Object ID</DetailLabel>
                                    <MonoValue
                                      value={entityId}
                                      copied={copiedId === `ent-${log.id}`}
                                      onCopy={() => copyToClipboard(entityId, `ent-${log.id}`)}
                                    />
                                  </>
                                )}

                                {typeof meta.entityType === 'string' && (
                                  <>
                                    <DetailLabel>Object type</DetailLabel>
                                    <Typography variant="body2">{String(meta.entityType)}</Typography>
                                  </>
                                )}

                                {typeof meta.path === 'string' && (
                                  <>
                                    <DetailLabel>Path</DetailLabel>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {String(meta.path)}
                                    </Typography>
                                  </>
                                )}

                                <DetailLabel>IP address</DetailLabel>
                                <Typography variant="body2">
                                  {log.ipAddress ? log.ipAddress.replace(/^::ffff:/, '') : '—'}
                                </Typography>

                                {log.userAgent && (
                                  <>
                                    <DetailLabel>User agent</DetailLabel>
                                    <Typography variant="caption" color="text.secondary">
                                      {log.userAgent}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      )}

      {/* Cleanup confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)}>
        <DialogTitle>Delete selected activity logs?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete <strong>{selectedIds.length}</strong> selected record
            {selectedIds.length === 1 ? '' : 's'}. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleCleanup} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={22} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
